import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { lawFieldsDE, lawFieldsEN, lawFieldsFR } from '@/i18n/lawFields'

/**
 * Composable for translating law fields based on the current locale
 *
 * :return: Object containing translateLawField function
 */
export function useLawFieldTranslation() {
  const { locale } = useI18n()

  /**
   * Translate a law field based on the current locale
   * Falls back to German key if no translation exists
   *
   * :param lawField: The law field key to translate (e.g., 'Arbeitsrecht')
   * :return: Translated law field or original German key if no translation exists
   */
  const translateLawField = (lawField: string | null | undefined): string => {
    if (!lawField) return ''

    // Access locale.value to ensure reactivity
    const currentLocale = locale.value

    // For French locale, try French translation, otherwise return German key
    if (currentLocale === 'fr') {
      return lawFieldsFR[lawField] || lawField
    }

    // For English locale, try English translation, otherwise return German key
    if (currentLocale === 'en') {
      return lawFieldsEN[lawField] || lawField
    }

    // For German locale or any other, use German translations
    return lawFieldsDE[lawField] || lawField
  }

  // Computed getter for current locale's translations map
  const lawFieldTranslations = computed(() => {
    if (locale.value === 'fr') return lawFieldsFR
    if (locale.value === 'en') return lawFieldsEN
    return lawFieldsDE
  })

  return {
    translateLawField,
    lawFieldTranslations
  }
}
